import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { getAdminTradovateErrors, useQuery } from "wasp/client/operations";
import { Card, CardContent } from "../../../client/components/ui/card";
import useTranslation from "../../../client/hooks/useTranslation";

// Même défaut que les cartes quota voisines : les chiffres se lisent côte à
// côte, et des fenêtres différentes les rendraient incomparables.
const DEFAULT_HOURS = 48;

type AllowedHours = 24 | 48 | 168 | 720;

/**
 * Erreurs et pénalités Tradovate : QUAND et POURQUOI le quota est tapé (B1).
 *
 * La carte répond à trois questions, dans l'ordre où on se les pose pendant
 * un incident : est-ce qu'on est pénalisé en ce moment (tuiles), sur quel
 * endpoint ça coince (ventilation), et quel est le déroulé exact (journal
 * horodaté avec p-time et phase). L'état vide est un message POSITIF : zéro
 * pénalité est précisément ce qu'on veut constater.
 */
const TradovateErrorsCard = () => {
  const t = useTranslation();
  const [hours, setHours] = useState<AllowedHours>(DEFAULT_HOURS);
  const { data } = useQuery(getAdminTradovateErrors, { hours });

  const ranges: { value: AllowedHours; label: string }[] = [
    { value: 24, label: t.adminDashboard.quotaHistoryRange24h },
    { value: 48, label: t.adminDashboard.quotaHistoryRange48h },
    { value: 168, label: t.adminDashboard.quotaHistoryRange7d },
    { value: 720, label: t.adminDashboard.quotaHistoryRange30d },
  ];

  // Retypage à la frontière, comme les cartes voisines : SuperJSON élargit
  // chaque champ à toute sa union de valeurs sérialisables.
  const totalRateLimitHits = Number(data?.totalRateLimitHits ?? 0);
  const totalCalls = Number(data?.totalCalls ?? 0);
  const captchaCount = Number(data?.captchaCount ?? 0);
  const retryFailures = Number(data?.retryFailures ?? 0);
  const byEndpoint = (data?.byEndpoint ?? []) as Array<
    Record<string, string | number | null>
  >;
  const recent = (data?.recent ?? []) as Array<
    Record<string, string | number | boolean | null>
  >;

  const phaseLabel = (phase: unknown): string =>
    phase === "retry"
      ? t.adminDashboard.tradovateErrorsPhaseRetry
      : t.adminDashboard.tradovateErrorsPhaseInitial;

  return (
    <div>
      <h2 className="text-foreground mb-1 flex items-center gap-2 text-lg font-semibold">
        <AlertTriangle className="h-4 w-4" />
        {t.adminDashboard.tradovateErrorsTitle}
      </h2>
      <p className="text-muted-foreground mb-4 text-xs">
        {t.adminDashboard.tradovateErrorsHint}
      </p>

      <Card>
        <CardContent className="space-y-6 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="text-muted-foreground">
                {t.adminDashboard.tradovateErrorsRateLimits}{" "}
                <span
                  className={`font-mono font-semibold ${
                    totalRateLimitHits > 0 ? "text-destructive" : "text-foreground"
                  }`}
                >
                  {totalRateLimitHits}
                </span>
              </span>
              <span className="text-muted-foreground">
                {t.adminDashboard.tradovateErrorsCalls}{" "}
                <span className="text-foreground font-mono">{totalCalls}</span>
              </span>
              {/* Le p-captcha est la seule pénalité qu'aucune attente ne résout :
                  affiché seulement s'il existe, en alerte franche. */}
              {captchaCount > 0 && (
                <span className="text-destructive font-semibold">
                  {t.adminDashboard.tradovateErrorsCaptcha}{" "}
                  <span className="font-mono">{captchaCount}</span>
                </span>
              )}
              {retryFailures > 0 && (
                <span
                  className="text-muted-foreground"
                  title={t.adminDashboard.tradovateErrorsRetryHint}
                >
                  {t.adminDashboard.tradovateErrorsRetryFailures}{" "}
                  <span className="text-foreground font-mono">{retryFailures}</span>
                </span>
              )}
            </div>

            <div className="flex gap-1">
              {ranges.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setHours(r.value)}
                  className={`rounded px-2 py-1 text-xs transition-colors ${
                    hours === r.value
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {totalRateLimitHits === 0 && recent.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              {t.adminDashboard.tradovateErrorsEmpty}
            </p>
          ) : (
            <>
              {/* --- Quel endpoint encaisse les pénalités --- */}
              {byEndpoint.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-muted-foreground border-border border-b text-left text-xs">
                        <th className="pb-2 font-normal">
                          {t.adminDashboard.endpointBreakdownEndpointCol}
                        </th>
                        <th className="pb-2 text-right font-normal">
                          {t.adminDashboard.tradovateErrorsHitsCol}
                        </th>
                        <th className="pb-2 text-right font-normal">
                          {t.adminDashboard.tradovateErrorsCallsCol}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {byEndpoint.map((row) => (
                        <tr key={String(row.endpointKey)} className="border-border/50 border-b">
                          <td className="py-1.5 font-mono text-xs">{String(row.endpointKey)}</td>
                          <td className="text-destructive py-1.5 text-right font-mono">
                            {Number(row.rateLimitHits ?? 0)}
                          </td>
                          <td className="py-1.5 text-right font-mono">
                            {Number(row.calls ?? 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* --- Le déroulé exact : journal horodaté --- */}
              {recent.length > 0 && (
                <div>
                  <h3 className="text-foreground mb-2 text-sm font-medium">
                    {t.adminDashboard.tradovateErrorsRecentTitle}
                  </h3>
                  <div className="max-h-80 overflow-x-auto overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-muted-foreground border-border border-b text-left text-xs">
                          <th className="pb-2 font-normal">
                            {t.adminDashboard.tradovateErrorsTimeCol}
                          </th>
                          <th className="pb-2 font-normal">
                            {t.adminDashboard.endpointBreakdownEndpointCol}
                          </th>
                          <th className="pb-2 text-right font-normal">
                            {t.adminDashboard.tradovateErrorsStatusCol}
                          </th>
                          <th className="pb-2 text-right font-normal">
                            {t.adminDashboard.tradovateErrorsPenaltyCol}
                          </th>
                          <th className="pb-2 font-normal">
                            {t.adminDashboard.tradovateErrorsPhaseCol}
                          </th>
                          <th className="pb-2 font-normal">
                            {t.adminDashboard.tradovateErrorsHostCol}
                          </th>
                          <th className="pb-2 font-normal">
                            {t.adminDashboard.tradovateErrorsUserCol}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent.map((row, index) => (
                          <tr
                            key={`${String(row.observedAt)}-${index}`}
                            className="border-border/50 border-b"
                          >
                            <td className="py-1.5 font-mono text-xs">
                              {new Date(String(row.observedAt)).toLocaleString()}
                            </td>
                            <td className="py-1.5 font-mono text-xs">
                              {String(row.endpointKey)}
                            </td>
                            <td className="py-1.5 text-right font-mono">
                              {Number(row.status ?? 0)}
                              {row.captcha === true && (
                                <span className="text-destructive ml-1 font-semibold">
                                  {t.adminDashboard.tradovateErrorsCaptchaTag}
                                </span>
                              )}
                            </td>
                            <td className="py-1.5 text-right font-mono">
                              {row.pTimeSeconds === null || row.pTimeSeconds === undefined
                                ? "-"
                                : `${Number(row.pTimeSeconds)}s`}
                            </td>
                            <td className="py-1.5 text-xs">{phaseLabel(row.phase)}</td>
                            <td className="py-1.5 font-mono text-xs">
                              {row.env === null || row.env === undefined ? "-" : String(row.env)}
                            </td>
                            <td className="py-1.5 text-xs">
                              {row.userEmail === null || row.userEmail === undefined
                                ? "-"
                                : String(row.userEmail)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TradovateErrorsCard;
